import React, { useRef, useState, useCallback, useEffect } from 'react';

const VirtualJoystick = ({ onMove, onJump, size = 120 }) => {
  const joystickRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const centerRef = useRef({ x: 0, y: 0 });

  const maxDistance = size / 2 - 20;

  const handleStart = useCallback((clientX, clientY) => {
    if (!joystickRef.current) return;
    
    const rect = joystickRef.current.getBoundingClientRect();
    centerRef.current = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
    setIsDragging(true);
    
    // Calculate initial position
    const deltaX = clientX - centerRef.current.x;
    const deltaY = clientY - centerRef.current.y;
    updatePosition(deltaX, deltaY);
  }, []);

  const updatePosition = useCallback((deltaX, deltaY) => {
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const clampedDistance = Math.min(distance, maxDistance);
    const angle = Math.atan2(deltaY, deltaX);
    
    const x = Math.cos(angle) * clampedDistance;
    const y = Math.sin(angle) * clampedDistance;
    
    setPosition({ x, y });
    
    // Calculate normalized direction (-1 to 1)
    const normalizedX = x / maxDistance;
    const normalizedY = y / maxDistance;
    
    // Determine movement (2.5D: up/down = depth, left/right = horizontal)
    const moveLeft = normalizedX < -0.3;
    const moveRight = normalizedX > 0.3;
    const moveUp = normalizedY < -0.4;    // Depth: walk back
    const moveDown = normalizedY > 0.4;   // Depth: walk forward
    
    onMove({ left: moveLeft, right: moveRight, up: moveUp, down: moveDown });
  }, [maxDistance, onMove, onJump]);

  const handleMove = useCallback((clientX, clientY) => {
    if (!isDragging) return;
    
    const deltaX = clientX - centerRef.current.x;
    const deltaY = clientY - centerRef.current.y;
    updatePosition(deltaX, deltaY);
  }, [isDragging, updatePosition]);

  const handleEnd = useCallback(() => {
    setIsDragging(false);
    setPosition({ x: 0, y: 0 });
    onMove({ left: false, right: false, up: false, down: false });
  }, [onMove]);

  // Touch events
  const onTouchStart = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY);
  }, [handleStart]);

  const onTouchMove = useCallback((e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMove(touch.clientX, touch.clientY);
  }, [handleMove]);

  const onTouchEnd = useCallback((e) => {
    e.preventDefault();
    handleEnd();
  }, [handleEnd]);

  // Mouse events for testing on desktop
  const onMouseDown = useCallback((e) => {
    handleStart(e.clientX, e.clientY);
  }, [handleStart]);

  useEffect(() => {
    const handleMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const handleMouseUp = () => handleEnd();

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMove, handleEnd]);

  return (
    <div
      ref={joystickRef}
      className="relative touch-none select-none"
      style={{ width: size, height: size }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      data-testid="virtual-joystick"
    >
      {/* Base circle - transparent */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'rgba(20, 241, 149, 0.08)',
          border: '2px solid rgba(20, 241, 149, 0.25)',
        }}
      >
        {/* Direction indicators */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 text-[#14F195]/30 text-xs font-game">↑</div>
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[#14F195]/30 text-xs font-game">↓</div>
        <div className="absolute left-2 top-1/2 -translate-y-1/2 text-[#14F195]/30 text-xs font-game">←</div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 text-[#14F195]/30 text-xs font-game">→</div>
      </div>
      
      {/* Thumb/knob - semi-transparent */}
      <div
        className="absolute rounded-full transition-transform duration-75"
        style={{
          width: 50,
          height: 50,
          left: '50%',
          top: '50%',
          transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          background: isDragging 
            ? 'rgba(20, 241, 149, 0.5)' 
            : 'rgba(20, 241, 149, 0.25)',
          border: '2px solid rgba(20, 241, 149, 0.6)',
          boxShadow: isDragging 
            ? '0 0 15px rgba(20, 241, 149, 0.4)' 
            : 'none',
        }}
      />
    </div>
  );
};

const ActionButton = ({ 
  onPress, 
  onRelease, 
  label, 
  icon,
  color = '#14F195',
  size = 70,
  isPressed = false 
}) => {
  const [pressed, setPressed] = useState(false);

  const handleStart = useCallback((e) => {
    e.preventDefault();
    setPressed(true);
    onPress?.();
  }, [onPress]);

  const handleEnd = useCallback((e) => {
    e.preventDefault();
    setPressed(false);
    onRelease?.();
  }, [onRelease]);

  // Convert hex to rgba for transparency
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <button
      className="touch-none select-none rounded-full flex items-center justify-center transition-all duration-100 active:scale-95"
      style={{
        width: size,
        height: size,
        background: pressed 
          ? hexToRgba(color, 0.4)
          : hexToRgba(color, 0.1),
        border: `2px solid ${hexToRgba(color, pressed ? 0.7 : 0.35)}`,
        boxShadow: pressed 
          ? `0 0 15px ${hexToRgba(color, 0.3)}` 
          : 'none',
        color: hexToRgba(color, pressed ? 1 : 0.7),
      }}
      onTouchStart={handleStart}
      onTouchEnd={handleEnd}
      onMouseDown={handleStart}
      onMouseUp={handleEnd}
      onMouseLeave={() => {
        if (pressed) {
          setPressed(false);
          onRelease?.();
        }
      }}
      data-testid={`action-btn-${label.toLowerCase()}`}
    >
      <div className="flex flex-col items-center">
        {icon && <span className="text-xl">{icon}</span>}
        <span className="font-game text-[10px]">{label}</span>
      </div>
    </button>
  );
};

const TouchControls = ({ 
  onMoveChange, 
  onJump, 
  onShootStart, 
  onShootEnd, 
  onGrenade,
  isVisible = true 
}) => {
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  const handleMove = useCallback((movement) => {
    onMoveChange?.(movement);
  }, [onMoveChange]);

  const handleJump = useCallback(() => {
    onJump?.();
  }, [onJump]);

  if (!isVisible) return null;

  // Compact layout for landscape
  const joystickSize = isLandscape ? 90 : 110;
  const fireSize = isLandscape ? 55 : 65;
  const jumpSize = isLandscape ? 45 : 50;
  const grenadeSize = isLandscape ? 45 : 50;

  return (
    <div 
      className="w-full pointer-events-none z-50 mt-3"
      style={{ 
        paddingLeft: '12px',
        paddingRight: '12px',
      }}
      data-testid="touch-controls"
    >
      <div className="flex justify-between items-start w-full">
        {/* Left side - Joystick */}
        <div className="pointer-events-auto">
          <VirtualJoystick 
            onMove={handleMove}
            onJump={handleJump}
            size={joystickSize}
          />
        </div>

        {/* Right side - Action buttons */}
        <div className={`pointer-events-auto flex ${isLandscape ? 'flex-row items-center gap-2' : 'flex-col items-center gap-2'}`}>
          {/* Jump button */}
          <ActionButton
            label="JUMP"
            icon="↑"
            color="#5AC8FA"
            size={jumpSize}
            onPress={onJump}
          />
          
          {/* Fire and Grenade */}
          <div className={`flex ${isLandscape ? 'flex-col gap-2' : 'flex-row gap-3'}`}>
            <ActionButton
              label="FIRE"
              icon="●"
              color="#14F195"
              size={fireSize}
              onPress={onShootStart}
              onRelease={onShootEnd}
            />
            <ActionButton
              label="NADE"
              icon="◉"
              color="#FF9500"
              size={grenadeSize}
              onPress={onGrenade}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TouchControls;
export { VirtualJoystick, ActionButton };
